// =============================================================================
// Pintravel — Query client
// =============================================================================
// Module-level rather than created inside the provider, because the offline
// layer has to seed it from SQLite the moment a session opens — and that
// happens in a Zustand store, outside the React tree.
// =============================================================================

import { QueryClient } from '@tanstack/react-query';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 2 minutes — no refetch during this window
        staleTime: 1000 * 60 * 2,
        // Keep inactive cache entries for 10 minutes before garbage collection
        gcTime: 1000 * 60 * 10,
        // Retry failed requests up to 2 times with exponential backoff
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000),
        // Refetch when the window regains focus (useful for stale data after backgrounding)
        refetchOnWindowFocus: true,
        // Don't refetch on reconnect by default — the sync engine handles this
        refetchOnReconnect: false,
        // Run the query even when the browser reports no connection, instead of
        // parking it in `paused`. Offline the request fails fast and the hook's
        // own fallback serves what SQLite has; parked queries just showed a
        // spinner that never resolved.
        networkMode: 'offlineFirst',
      },
      mutations: {
        // Retry once, but never on a 4xx: the server has rejected the request
        // itself, so repeating it only delays the error reaching the user. A
        // rejected checklist item used to spin for seconds before failing.
        retry: (failureCount, error) => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status !== undefined && status >= 400 && status < 500) return false;
          return failureCount < 1;
        },
        retryDelay: 1000,
        networkMode: 'offlineFirst',
      },
    },
  });
}

/** The one client the app and the offline layer both talk to. */
export const queryClient = createQueryClient();

export default queryClient;
